import { task } from 'ember-concurrency';
import groupBy from 'lodash/groupBy';
import {
  buildCFApiParamPrefix,
  buildCustomFieldFilter,
  CustomFieldDefinitionEvents,
  CustomFieldDefinitionResourceName
} from 'volta/models/custom-field-definition';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { inFilter } from 'volta/utils/filters-utils';

import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { TError } from 'volta/utils/error-utils';
import type CustomFieldDefinition from 'volta/models/custom-field-definition';
import type { ICollectionProperty } from 'volta/models/types/collection-view';
import type { CFTblName } from 'volta/models/types/custom-field-definition';
import type EventStreamService from 'volta/services/event-stream';
import type StoreService from 'volta/services/store';
import type SessionUserService from 'volta/services/session-user';
import type { SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import { normalize } from 'volta/utils/api/serialize-and-push';
import type Owner from '@ember/owner';

const EntityTables: CFTblName[] = [
  'inventories',
  'suppliers',
  'workshops',
  'planning_orders',
  'sources'
];

export default class CustomFieldDefinitionsService extends Service {
  // Services
  @service store!: StoreService;
  @service eventStream!: EventStreamService;
  @service sessionUser!: SessionUserService;

  @tracked definitions: Partial<Record<CFTblName, CustomFieldDefinition[] | undefined>> = {};

  constructor(owner: Owner) {
    super(owner);

    // Init definitions state
    EntityTables.forEach((e: CFTblName) => (this.definitions[e] = undefined));

    this.eventStream.addHandler(
      CustomFieldDefinitionResourceName.plural,
      CustomFieldDefinitionEvents.CustomFieldDefinitionCreated,
      this.handleCustomFieldDefinitionCreated,
      this
    );
    this.eventStream.addHandler(
      CustomFieldDefinitionResourceName.plural,
      CustomFieldDefinitionEvents.CustomFieldDefinitionUpdated,
      this.handleCustomFieldDefinitionUpdated,
      this
    );
    this.eventStream.addHandler(
      CustomFieldDefinitionResourceName.plural,
      CustomFieldDefinitionEvents.CustomFieldDefinitionDeleted,
      this.handleCustomFieldDefinitionDeleted,
      this
    );
  }

  private handleCustomFieldDefinitionCreated(payload: SingleResourceDoc) {
    const cf = normalize('custom-field-definition', payload) as CustomFieldDefinition;
    this.addOne(cf);
  }

  private handleCustomFieldDefinitionUpdated(payload: SingleResourceDoc) {
    const cf = normalize('custom-field-definition', payload) as CustomFieldDefinition;
    this.addOne(cf);
  }

  private handleCustomFieldDefinitionDeleted(payload: SingleResourceDoc) {
    const tblName = payload.data.attributes?.['tblName'];
    if (payload.data.id && tblName) {
      this.removeById(tblName as CFTblName, payload.data.id);
    }
  }

  async fetchSkusDefinitions() {
    return this.task.perform(['inventories'], false);
  }

  async fetchSuppliersDefinitions() {
    return this.task.perform(['suppliers'], false);
  }

  async fetchWorkshopsDefinitions() {
    return this.task.perform(['workshops'], false);
  }

  async fetchPODefinitions() {
    return this.task.perform(['planning_orders', 'inventories'], false);
  }

  async fetchSourceDefinitions() {
    return this.task.perform(['sources'], false);
  }

  get skusDefinitions() {
    return getAuthorisedDefinitions(
      this.definitions['inventories'],
      this.sessionUser.user.warehouseIds
    );
  }

  get suppliersDefinitions() {
    return this.definitions['suppliers'] ?? [];
  }

  get workshopsDefinitions() {
    return this.definitions['workshops'] ?? [];
  }

  get poDefinitions() {
    return getAuthorisedDefinitions(
      this.definitions['planning_orders'],
      this.sessionUser.user.warehouseIds
    );
  }

  get sourceDefinitions() {
    return this.definitions['sources'] ?? [];
  }

  skusCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.skusDefinitions, apiPrefix);
  }

  skusCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.skusDefinitions, apiPrefix);
  }

  suppliersCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.suppliersDefinitions, apiPrefix);
  }

  suppliersCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.suppliersDefinitions, apiPrefix);
  }

  workshopsCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.workshopsDefinitions, apiPrefix);
  }

  workshopsCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.workshopsDefinitions, apiPrefix);
  }

  sourcesCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.sourceDefinitions, apiPrefix);
  }

  poCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.poDefinitions, apiPrefix);
  }

  poCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.poDefinitions, apiPrefix);
  }

  sourceCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.sourceDefinitions, apiPrefix);
  }

  addOne(def: CustomFieldDefinition) {
    const tableDefinition = this.definitions[def.tblName];
    const defExists = tableDefinition?.find((d: CustomFieldDefinition) => d.id === def.id);
    if (defExists) {
      return;
    }
    this.definitions[def.tblName] = tableDefinition ? [...tableDefinition, def] : [def];
  }

  removeOne(def: CustomFieldDefinition) {
    this.removeById(def.tblName, def.id!);
  }

  removeById(tblName: CFTblName, id: string) {
    const tableDefinition = this.definitions[tblName];
    if (tableDefinition?.length) {
      this.definitions[tblName] = tableDefinition.filter((d: CustomFieldDefinition) => d.id !== id);
    }
  }

  task = task(async (tblNames: CFTblName[] = [], force: boolean = false) => {
    try {
      const tblNamesValues = tblNames.reduce(
        (acc: { cached: CFTblName[]; notCached: CFTblName[] }, tblName: CFTblName) => {
          const name = `${tblName}`.toLowerCase() as CFTblName;
          if (EntityTables.includes(name)) {
            if (this.definitions[name]) {
              acc.cached.push(name);
            } else {
              acc.notCached.push(name);
            }
          }
          return acc;
        },
        { cached: [], notCached: [] }
      );

      if (tblNamesValues.cached.length === 0 && tblNamesValues.notCached.length === 0) {
        return;
      }

      if (tblNamesValues.notCached.length > 0) {
        const query = {
          filter: {
            tblName: inFilter(force ? tblNames : tblNamesValues.notCached)
          },
          page: { offset: 0, limit: 1000 },
          sort: 'order',
          include: 'warehouses'
        };

        const definitions = await this.store.query<CustomFieldDefinition>(
          'custom-field-definition',
          // @ts-ignore
          query
        );
        this.definitions = {
          ...this.definitions,
          ...groupBy(definitions.slice(), 'tblName')
        };
      }

      return Object.fromEntries(
        tblNames.filter((key) => key in this.definitions).map((key) => [key, this.definitions[key]])
      );
    } catch (error) {
      return defaultErrorHandler(error as TError);
    }
  });
}

/**
 * Build custom fields filters from custom field definitions
 * @param definitions Custom field definitions
 * @param apiPrefix Api prefix
 * @returns Custom fields filters
 */
const customFieldsFilters = (definitions?: CustomFieldDefinition[], apiPrefix?: string) => {
  return (definitions ?? []).reduce<TFilterDefinition[]>((acc, def) => {
    const filter = buildCustomFieldFilter(def, apiPrefix);
    if (filter) {
      acc.push(filter);
    }
    return acc;
  }, []);
};

/**
 * Build BvTable columns from custom field definitions
 * @param definitions Custom field definitions
 * @param apiPrefix Api prefix
 * @returns Custom fields BvTable columns
 */
const customFieldsColumns = (definitions?: CustomFieldDefinition[], apiPrefix?: string) => {
  return (
    definitions?.reduce<ICollectionProperty[]>((acc, def) => {
      if (!def) {
        return acc;
      }

      const { name, slug, fieldType } = def;
      const propDef: ICollectionProperty = {
        property: buildCFApiParamPrefix(apiPrefix) + slug,
        title: name,
        dataType: fieldType,
        customFieldDefinition: def,
        isCustomField: true
      };

      acc.push(propDef);

      return acc;
    }, []) ?? []
  );
};

/**
 * Get authorised definitions checking constraints
 * @param definitions Custom field definitions
 * @param warehouseIds Warehouse ids
 * @returns  Authorised definitions
 */
const getAuthorisedDefinitions = (
  definitions: CustomFieldDefinition[] = [],
  warehouseIds: string[] = []
) => {
  if (warehouseIds.length === 0) {
    return definitions;
  }

  return definitions.filter((d) => {
    if ((d.warehouseIds ?? []).length > 0) {
      return d.warehouseIds.some((id) => id && warehouseIds.includes(id));
    }
    return true;
  });
};

declare module '@ember/service' {
  interface Registry {
    'custom-field-definitions-service': CustomFieldDefinitionsService;
  }
}
